// ─────────────────────────────────────────────────────────────────────────────
// CROSS-READS — your behaviour against the market scenario (plan 5.5).
// The Wallace half is drawn as designed, wording included, as a placeholder
// (U3). Logic: Buckly.dc.html:985-1004; markup: :453-491.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { sx } from './primitives';
import { arthurRows, wallaceRows } from './placeholders';

type RowSrc = { dot: string; title: string; desc: string; vLabel: string; vColor: string };

const verdict = (label: string, c: string) => `flex:none; font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.1em; text-transform:uppercase; color:rgb(${c}); padding:5px 13px; border-radius:999px; border:1px solid rgba(${c},0.4);`;
const mkRow = ({ dot, title, desc, vLabel, vColor }: RowSrc) => ({
  title, desc, vLabel,
  dotStyle: `flex:none; width:9px; height:9px; border-radius:50%; background:rgb(${dot}); box-shadow:0 0 8px rgba(${dot},0.7);`,
  vStyle: verdict(vLabel, vColor),
});

export function CrossReadsTab() {
  const v = { wallaceRows: wallaceRows.map(mkRow), arthurRows: arthurRows.map(mkRow) };

  return (
    // ⟨design Buckly.dc.html:453-491 — generated by dc2tsx.py; regenerate, never hand-edit⟩
    <div style={sx("padding-top:24px;")}>
      <p style={sx("margin:0 0 26px; font-size:14.5px; line-height:1.65; color:rgba(218,226,228,0.72); max-width:1000px;")}>
        {"Buckly doesn't watch you in the abstract — she reads your behaviour "}
        <span style={sx("color:#6fc0f2;")}>inside the exact market scenario the lenses are reading</span>
        {". Arthur and Wallace journal their per-trade context up; Buckly finds your patterns against it."}
      </p>
      {/* AGAINST WALLACE */}
      <div style={sx("font-family:'JetBrains Mono',monospace; font-size:11px; letter-spacing:0.14em; text-transform:uppercase; color:#6fc0f2; margin-bottom:14px;")}>{"Against Wallace's order flow"}</div>
      <div style={sx("display:flex; flex-direction:column; gap:11px; margin-bottom:30px;")}>
        {v.wallaceRows.map((r, rIdx) => (
          <div key={rIdx} style={sx("display:flex; align-items:center; gap:18px; padding:16px 22px; border-radius:12px; background:linear-gradient(180deg, rgba(10,16,28,0.66) 0%, rgba(6,9,18,0.76) 100%); border:1px solid rgba(60,150,230,0.16);")}>
            <span style={sx(r.dotStyle)} />
            <span style={sx("flex:none; min-width:240px; font-size:16px; font-weight:600; color:#f1f6f7;")}>{r.title}</span>
            <span style={sx("flex:1; font-family:'JetBrains Mono',monospace; font-size:12.5px; color:rgba(200,210,212,0.6);")}>{r.desc}</span>
            <span style={sx(r.vStyle)}>{r.vLabel}</span>
          </div>
        ))}
      </div>
      {/* AGAINST ARTHUR */}
      <div style={sx("font-family:'JetBrains Mono',monospace; font-size:11px; letter-spacing:0.14em; text-transform:uppercase; color:#6fc0f2; margin-bottom:14px;")}>{"Against Arthur's strategy"}</div>
      <div style={sx("display:flex; flex-direction:column; gap:11px; margin-bottom:30px;")}>
        {v.arthurRows.map((r, rIdx) => (
          <div key={rIdx} style={sx("display:flex; align-items:center; gap:18px; padding:16px 22px; border-radius:12px; background:linear-gradient(180deg, rgba(10,16,28,0.66) 0%, rgba(6,9,18,0.76) 100%); border:1px solid rgba(60,150,230,0.16);")}>
            <span style={sx(r.dotStyle)} />
            <span style={sx("flex:none; min-width:240px; font-size:16px; font-weight:600; color:#f1f6f7;")}>{r.title}</span>
            <span style={sx("flex:1; font-family:'JetBrains Mono',monospace; font-size:12.5px; color:rgba(200,210,212,0.6);")}>{r.desc}</span>
            <span style={sx(r.vStyle)}>{r.vLabel}</span>
          </div>
        ))}
      </div>
      {/* INTERSECTION */}
      <div style={sx("padding:26px; border-radius:14px; background:linear-gradient(180deg, rgba(14,24,40,0.74) 0%, rgba(8,13,24,0.84) 100%); border:1px solid rgba(60,150,230,0.34); box-shadow:inset 0 1px 0 rgba(255,255,255,0.04), 0 0 40px rgba(40,110,200,0.08);")}>
        <div style={sx("display:flex; align-items:center; gap:13px; margin-bottom:16px;")}>
          <div style={sx("flex:none; width:38px; height:38px; border-radius:10px; border:1px solid rgba(95,184,240,0.45); display:flex; align-items:center; justify-content:center;")}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#5fb8f0" strokeWidth="1.6" strokeLinejoin="round">
              <path d="M12 3 L21 8 L12 13 L3 8 Z" />
              <path d="M3 13 L12 18 L21 13" />
            </svg>
          </div>
          <span style={sx("font-size:19px; font-weight:600; color:#f3f7f8;")}>The intersection — where it all lands</span>
        </div>
        <p style={sx("margin:0 0 14px; font-size:15.5px; line-height:1.65; color:rgba(222,228,232,0.82);")}>
          {"Right now: Wallace sees a clean fade forming, Mack flags a high-vol CPI print, and your data says "}
          <span style={sx("color:#6fc0f2;")}>this is exactly the condition where you overtrade and size up</span>
          . Real opportunity — and your highest-risk behavioural setup, at the same moment. Size with that in mind.
        </p>
        <div style={sx("font-size:13px; color:rgba(200,210,212,0.5); line-height:1.55; margin-bottom:16px;")}>This is the read no terminal or journaling tool can produce — it needs the per-trader memory and the market context in one place.</div>
        <div style={sx("display:flex; align-items:center; gap:9px; font-family:'JetBrains Mono',monospace; font-size:11.5px; color:rgba(200,210,212,0.55); flex-wrap:wrap;")}>
          <span style={sx("width:6px; height:6px; border-radius:50%; background:#5fb8f0;")} />
          {"Wallace (order flow) · Mack (regime) · your behavioural memory — composed through one MCR retrieval"}
        </div>
      </div>
    </div>
    // ⟨/design⟩
  );
}
